/**
 * Tagada Rx — legal-representative identity verification.
 *
 * Tagada remits real money to this affiliate, so the person who signs for
 * the brand has to be verified before the first payout leaves. The SumSub
 * WebSDK is embedded inline; the decision itself arrives asynchronously
 * through the platform webhook, so the query polls while a review is open.
 *
 * Staff (account managers) can unlock an affiliate without a SumSub
 * decision — the reason is recorded on the profile.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, ShieldAlert, ShieldCheck, Unlock, XCircle } from 'lucide-react';
import SumsubWebSdk from '@sumsub/websdk-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAccountManager } from '@/hooks/use-account-manager';
import {
  useRxBypassPayoutKyc,
  useRxPayoutProfile,
  useRxRefreshPayoutKyc,
  useRxStartPayoutKyc,
  type RxPayoutProfile,
} from '@/lib/api/rx';

export function RxKycStep({
  profile,
  onChanged,
}: {
  profile: RxPayoutProfile;
  onChanged: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const startKyc = useRxStartPayoutKyc();
  const refreshKyc = useRxRefreshPayoutKyc();
  const bypassKyc = useRxBypassPayoutKyc();

  const [sdkToken, setSdkToken] = React.useState<string | null>(null);
  const [bypassReason, setBypassReason] = React.useState('');
  const [showBypass, setShowBypass] = React.useState(false);

  const status = profile.profile.kycStatus;

  const handleStart = async () => {
    try {
      const session = await startKyc.mutateAsync();
      if (!session.sdkToken) {
        toast({
          title: t('kycStep.toast.startUnavailable.title'),
          description: t('kycStep.toast.startUnavailable.description'),
          variant: 'destructive',
        });
        return;
      }
      setSdkToken(session.sdkToken);
      onChanged();
    } catch (err) {
      toast({
        title: t('kycStep.toast.startFailed.title'),
        description: err instanceof Error ? err.message : t('kycStep.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  // SumSub tokens are short-lived; the SDK asks for a fresh one rather than
  // dropping the applicant mid-flow.
  const handleTokenExpiration = React.useCallback(async () => {
    const session = await startKyc.mutateAsync();
    return session.sdkToken ?? '';
  }, [startKyc]);

  const handleSdkMessage = React.useCallback(
    (type: string) => {
      if (type === 'idCheck.onApplicantStatusChanged') {
        refreshKyc.mutateAsync().then(onChanged).catch(() => undefined);
      }
    },
    [refreshKyc, onChanged],
  );

  const handleBypass = async () => {
    try {
      await bypassKyc.mutateAsync({ reason: bypassReason.trim() });
      setShowBypass(false);
      setBypassReason('');
      onChanged();
      toast({
        title: t('kycStep.toast.bypassSuccess.title'),
        description: t('kycStep.toast.bypassSuccess.description'),
      });
    } catch (err) {
      toast({
        title: t('kycStep.toast.bypassFailed.title'),
        description: err instanceof Error ? err.message : t('kycStep.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  if (status === 'approved' || status === 'bypassed') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <div className="text-sm">
          <div className="font-semibold text-emerald-800">
            {status === 'approved' ? t('kycStep.banner.approved') : t('kycStep.banner.bypassed')}
          </div>
          {status === 'bypassed' && profile.profile.kycBypassReason ? (
            <div className="mt-0.5 text-xs text-emerald-700">
              {profile.profile.kycBypassReason}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {status === 'rejected' ? (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="text-sm">
            <div className="font-semibold text-red-800">{t('kycStep.banner.rejected.title')}</div>
            <div className="mt-0.5 text-xs text-red-700">
              {t('kycStep.banner.rejected.description')}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm">
            <div className="font-semibold text-amber-800">
              {t('kycStep.banner.pending.title')}
            </div>
            <div className="mt-0.5 text-xs text-amber-700">
              {t('kycStep.banner.pending.description')}
            </div>
          </div>
        </div>
      )}

      {sdkToken ? (
        <div className="overflow-hidden rounded-lg border">
          <SumsubWebSdk
            accessToken={sdkToken}
            expirationHandler={handleTokenExpiration}
            options={{ addViewportTag: false, adaptIframeHeight: true }}
            onMessage={handleSdkMessage}
            onError={() => undefined}
          />
        </div>
      ) : (
        <Button onClick={handleStart} disabled={startKyc.isPending} size="sm">
          {startKyc.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck className="mr-2 h-4 w-4" />
          )}
          {status === 'rejected' ? t('kycStep.button.tryAgain') : t('kycStep.button.start')}
        </Button>
      )}

      {isAccountManager ? (
        <div className="border-t pt-3">
          {showBypass ? (
            <div className="space-y-2">
              <Label htmlFor="rx-kyc-bypass" className="text-xs">
                {t('kycStep.staff.label')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="rx-kyc-bypass"
                  value={bypassReason}
                  onChange={(e) => setBypassReason(e.target.value)}
                  placeholder={t('kycStep.staff.placeholder')}
                  className="h-8 text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBypass}
                  disabled={bypassReason.trim().length < 3 || bypassKyc.isPending}
                >
                  {bypassKyc.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t('kycStep.staff.confirm')
                  )}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowBypass(false)}>
                  {t('kycStep.staff.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setShowBypass(true)}>
              <Unlock className="mr-2 h-3.5 w-3.5" />
              {t('kycStep.staff.unlockToggle')}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
